import { ResponseError } from '@backstage/errors';
import {
  DiscoveryApi,
  FetchApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import type {
  CheatSheetApi,
  CheatSheetHint,
  CheatSheetHintInput,
} from './types';

export class CheatSheetRestApi implements CheatSheetApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly identityApi: IdentityApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    identityApi: IdentityApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.identityApi = options.identityApi;
  }

  private async fetchJson(path: string, init?: RequestInit): Promise<Response> {
    const baseUrl = await this.discoveryApi.getBaseUrl('neo4j-cheat-sheet');
    const { token } = await this.identityApi.getCredentials();
    const response = await this.fetchApi.fetch(`${baseUrl}/${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...init?.headers,
      },
    });
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response;
  }

  async listHints(): Promise<CheatSheetHint[]> {
    const response = await this.fetchJson('hints', { method: 'GET' });
    const body = await response.json();
    return body.hints as CheatSheetHint[];
  }

  async createHint(input: CheatSheetHintInput): Promise<CheatSheetHint> {
    const response = await this.fetchJson('hints', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return response.json();
  }

  async updateHint(
    id: string,
    input: CheatSheetHintInput,
  ): Promise<CheatSheetHint> {
    const response = await this.fetchJson(`hints/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    return response.json();
  }

  async deleteHint(id: string): Promise<void> {
    await this.fetchJson(`hints/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }
}
